/**
 * Page-builder expression aids (live preview + IntelliSense).
 *
 * The page builder lets non-developers bind component props with `{{ … }}`
 * expressions, but until now those fields had **no** discovery help: no
 * autocomplete (unlike the workflow editor) and no feedback until save. This
 * module closes that gap with three pure, side-effect-free helpers:
 *
 *  1. {@link buildPreviewScope} — assemble the live evaluation `Scope` the page
 *     runtime would see (`$user` / `$role` / `$queries` / `$page` / `$item` /
 *     `$index`) from the builder's already-fetched preview data. Mirrors
 *     `PageRenderer.buildScope` / `DataPanel` 1:1.
 *  2. {@link buildPageBindingsDts} — derive a TypeScript ambient `.d.ts` from
 *     that scope so Monaco offers schema-aware completions (`$queries.orders.`
 *     → the query's columns) exactly like the workflow editor does from a
 *     sample payload. Reuses {@link inferTsType}.
 *  3. {@link previewTemplate} — evaluate a template against the scope through
 *     the SAME sandboxed interpreter the portal uses at runtime
 *     (`lib/pageExpr`), returning a render-ready preview (`= 42`) or the error,
 *     so the author sees the result while typing.
 *
 * Pure (no React, no Monaco) → unit-tested cheaply. The page builder wires the
 * outputs into `ExpressionField` (`jsAmbientTypes` + `previewExpr`).
 */
import { inferTsType } from "@/components/workflows/expressionBindings";
import { isJsObjectNamespace, renderTemplate, type Scope } from "@/lib/pageExpr";
import { compileJsObjectsCached } from "@/lib/pageJsObjects";
import type { LibraryContext } from "@/lib/pageLibraries";
import { libraryGlobalsDts, type LibRef } from "@/lib/pageLibraryTypes";
import { FORMULA_DEFS } from "@/lib/pageFormulas";
import type { PageJsObject, PageVariable } from "@/lib/pageTypes";

/** A render-ready preview of an expression's value, or the evaluation error. */
export type ExprPreview =
  | { ok: true; display: string }
  | { ok: false; error: string; needsUpstream?: boolean };

interface QueryPreview {
  rows: Record<string, unknown>[];
  total: number;
  /** Alias of `rows` — the Appsmith-style data binding `{{ $queries.<name>.data }}`.
   *  Callers needn't supply it (it's DERIVED from `rows` in `buildPreviewScope`);
   *  the assembled scope always carries it so it MIRRORS the runtime
   *  (`PageRenderer.buildScope` exposes `.data` too) — the inspector preview
   *  resolves it (not `undefined`) AND the generated `.d.ts` autocompletes
   *  `$queries.<name>.data` (no more "expected property name"). */
  data?: Record<string, unknown>[];
}

export interface PreviewScopeInput {
  /** `$user` — the signed-in portal user (a stand-in in the builder preview). */
  user: { id: string; email: string; full_name?: string | null } | null;
  /** `$role` — the user's app role (`{ slug, name }`), null when unscoped. */
  role: { slug?: string; name?: string } | null;
  /** Per-query preview rows, keyed by query name (the builder's `previewQ`). */
  preview: Record<string, QueryPreview>;
  /** Declared page variables → seed `$page` with their initial values. */
  variables: PageVariable[];
  /** Implicit `$page` keys from inputs / row selection (no build-time value). */
  stateKeys?: string[];
  /** EVERY declared query NAME (ADR-0043) — so `$queries.<name>` autocompletes
   *  even before its preview data has loaded ("intellisense is a projection of
   *  the page graph", not just of the fetched rows). A name with no preview rows
   *  gets an empty `{rows:[], total:0}` shape. */
  queryNames?: string[];
  /** App-scoped `$global` keys the page references (ADR-0043 — discovered from
   *  its `global_value`/`global_remove` actions), seeded so `{{ $global.<key> }}`
   *  autocompletes + previews (as `undefined`; the store is runtime-only). */
  globalKeys?: string[];
  /** DECLARED app-wide `$global` variables (WS-4a, `apps.global_state`) → seed
   *  `$global` with their INITIAL values, so `{{ $global.<name> }}` both
   *  autocompletes AND previews its declared value (not `undefined`). The
   *  page-level twin is `variables` → `$page`. */
  globalVariables?: { name: string; initial?: unknown }[];
  /** Page JS Objects (ADR-0044) — compiled into the scope so a binding
   *  `{{ Utils.fn() }}` evaluates in the inspector preview, and declared in the
   *  ambient `.d.ts` so `{{ Utils. }}` autocompletes the object's functions. */
  jsObjects?: PageJsObject[];
  /** The app's external JS libraries (ADR-0046), loaded — their globals spread
   *  into the scope (so `{{ _.sum(...) }}` previews) + injected into the JS-Object
   *  compile. `undefined` until loaded (the inspector preview shows them empty,
   *  then re-renders). */
  libraries?: LibraryContext;
  /** The page's `$input` parameter names (R4-inputs) — declared `PageAst.inputs`
   *  or discovered `$input.<key>` usages. Seeded as `$input` (values `undefined`,
   *  the host supplies them at runtime) so `{{ $input.<key> }}` autocompletes +
   *  resolves in the inspector instead of reading as an unknown binding. */
  inputKeys?: string[];
  /** Columns of the nearest repeater (for `$item` typing) — empty otherwise. */
  itemColumns?: string[];
  /** A representative repeater row, so `$item.<col>` previews a real value. */
  itemSample?: Record<string, unknown>;
}

/**
 * Build the live `Scope` an expression sees on this page. Keys mirror the
 * portal runtime's binding surface so a preview here matches production:
 * `$user`, `$role`, `$queries.<name>.{rows,total}`, `$page.<var>`, plus
 * `$item` / `$index` when the selection sits inside a repeater.
 */
export function buildPreviewScope(input: PreviewScopeInput): Scope {
  const queries: Record<string, QueryPreview> = {};
  // Every DECLARED query name appears first (empty shape) so a not-yet-fetched
  // query still autocompletes; preview rows then fill in the typed columns.
  for (const name of input.queryNames ?? []) queries[name] = { rows: [], total: 0, data: [] };
  for (const [name, p] of Object.entries(input.preview)) {
    // `.data` aliases `.rows` (the U1 binding reads `q.rows`) — keep them the same
    // object so the preview value + the inferred `.d.ts` type match the runtime.
    queries[name] = { rows: p.rows, total: p.total, data: p.rows };
  }

  const page: Record<string, unknown> = {};
  for (const v of input.variables) page[v.name] = v.initial;
  // State keys (inputs / row selection) are written at runtime — surface them
  // as `undefined` so `$page.<key>` resolves (and types) without a value.
  for (const k of input.stateKeys ?? []) if (!(k in page)) page[k] = undefined;

  // `$global`: the app-scoped global bag. DECLARED variables (WS-4a) seed their
  // initial values (so `{{ $global.<name> }}` previews a real value); keys only
  // WRITTEN by actions seed `undefined` (runtime-only) — both autocomplete.
  const store: Record<string, unknown> = {};
  for (const v of input.globalVariables ?? []) store[v.name] = v.initial;
  for (const k of input.globalKeys ?? []) if (!(k in store)) store[k] = undefined;

  // `$item`: a real sample row when we have one, else a shape-only object from
  // the repeater's column names so completions still work (values show `—`).
  const item: Record<string, unknown> =
    input.itemSample ??
    Object.fromEntries((input.itemColumns ?? []).map((c) => [c, undefined]));

  // JS Objects (ADR-0044): compile to tagged namespaces and spread under their
  // names, so the inspector preview evaluates `{{ Utils.fn() }}` exactly like the
  // runtime (same `compileJsObjectsCached`). Keys are bare identifiers → no clash
  // with the `$`-prefixed bindings.
  const { namespaces } = compileJsObjectsCached(input.jsObjects, input.libraries);

  // `$input`: the page's declared/discovered parameters (R4-inputs). Runtime-only
  // values, so seed `undefined` — autocomplete + typing only.
  const inputs: Record<string, unknown> = {};
  for (const k of input.inputKeys ?? []) inputs[k] = undefined;

  return {
    $user: input.user,
    $role: input.role,
    $queries: queries,
    $page: page,
    $global: store,
    $item: item,
    $input: inputs,
    $index: 0,
    // ADR-0046 — the app's external library globals (trusted) so `{{ _.sum(...) }}`
    // previews in the inspector; JS Object namespaces spread AFTER (collision wins).
    ...(input.libraries?.globals ?? {}),
    ...namespaces,
  };
}

/** Known shapes for the bindings that don't vary with page data — typed
 *  explicitly so completions work even when the preview user/role is null. */
const USER_T = "{ id: string; email: string; full_name: string | null }";
const ROLE_T = "{ slug: string; name: string } | null";

const STORE_IDENT = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

/** Type literal for `$global` (ADR-0043 / WS-4a): each key is typed from its
 *  seeded VALUE (a DECLARED global carries its initial → a real type like
 *  `number[]`; an action-written key is `undefined` → `any`), plus an index
 *  signature because the store is dynamic (a key can be written from anywhere). */
function globalType(store: Record<string, unknown>): string {
  const keys = Object.keys(store);
  if (keys.length === 0) return "{ [key: string]: any }";
  const named = keys
    .map((k) => {
      const t = store[k] === undefined ? "any" : inferTsType(store[k]);
      return `${STORE_IDENT.test(k) ? k : JSON.stringify(k)}: ${t}`;
    })
    .join("; ");
  return `{ ${named}; [key: string]: any }`;
}

/** Ambient declarations for the page's JS Objects (ADR-0044): each compiled
 *  namespace in `scope` (a tagged object of functions) becomes a
 *  `declare const <Name>: { fn(...args: any[]): any; … }`, so `{{ Name. }}`
 *  autocompletes the real function names. */
function jsObjectDts(scope: Scope): string[] {
  const out: string[] = [];
  for (const [name, value] of Object.entries(scope)) {
    if (name.startsWith("$") || !isJsObjectNamespace(value)) continue;
    const fns = Object.keys(value as object).filter(
      (n) => typeof (value as Record<string, unknown>)[n] === "function",
    );
    const members = fns
      .filter((n) => STORE_IDENT.test(n))
      .map((n) => `${n}(...args: any[]): any`)
      .join("; ");
    out.push(`declare const ${name}: { ${members}${members ? "; " : ""}[key: string]: any };`);
  }
  return out;
}

/**
 * Generate the ambient `.d.ts` declaring every page binding, with `$queries` /
 * `$page` / `$item` typed from the live {@link buildPreviewScope} output (so
 * `$queries.orders.rows[0].` autocompletes the query's columns). Registered
 * into Monaco by `CodeEditor` under a fixed filename → replaces, never stacks.
 */
export function buildPageBindingsDts(scope: Scope, libraries?: LibRef[]): string {
  const queriesT = inferTsType(scope.$queries);
  const pageT = inferTsType(scope.$page);
  const itemT = inferTsType(scope.$item);
  const inputT = inferTsType(scope.$input ?? {});
  const storeT = globalType((scope.$global as Record<string, unknown>) ?? {});
  // JS Objects (ADR-0044): each compiled namespace in scope declares its
  // functions, so `{{ Utils. }}` autocompletes the real function names.
  const jsObjectDecls = jsObjectDts(scope);
  return [
    "// Tray page bindings — generated from the page's live preview data.",
    `declare const $user: ${USER_T};`,
    `declare const $role: ${ROLE_T};`,
    `declare const $queries: ${queriesT};`,
    `declare const $page: ${pageT};`,
    `declare const $global: ${storeT};`,
    `declare const $item: ${itemT};`,
    `declare const $input: ${inputT};`,
    "declare const $index: number;",
    ...jsObjectDecls,
    // ADR-0046 Slice 4 — declared external-JS-library globals (`_`, `dayjs`, …).
    libraryGlobalsDts(libraries),
    "",
    "// Spreadsheet-style formula functions (familiar to non-developers).",
    ...FORMULA_DEFS.map((f) => `/** ${f.doc} */\n${f.decl}`),
    "",
  ].join("\n");
}

/** A template is worth previewing only if it carries a `{{ }}` with real
 *  content — a bare `{{  }}` scaffold (or no braces) yields no preview. */
function hasMeaningfulExpr(tpl: string): boolean {
  return tpl.includes("{{") && /\{\{\s*[^}\s]/.test(tpl);
}

/**
 * Evaluate a `{{ }}` template against `scope` through the portal's sandboxed
 * interpreter and return a render-ready preview. Returns `null` when there's
 * nothing to preview (a fixed literal or an empty scaffold); `{ ok: false }`
 * with the message when the expression is invalid (live error feedback).
 */
export function previewTemplate(tpl: string, scope: Scope): ExprPreview | null {
  if (typeof tpl !== "string" || !hasMeaningfulExpr(tpl)) return null;
  try {
    return { ok: true, display: formatPreviewValue(renderTemplate(tpl, scope)) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Compact, never-throwing display string for a previewed value. Objects /
 *  arrays render as truncated JSON; scalars verbatim; blanks made visible. */
export function formatPreviewValue(value: unknown): string {
  let s: string;
  if (value === undefined) s = "undefined";
  else if (value === null) s = "null";
  else if (typeof value === "string") s = value === "" ? '""' : value;
  else if (typeof value === "number" || typeof value === "boolean") s = String(value);
  else {
    try {
      s = JSON.stringify(value) ?? String(value);
    } catch {
      s = String(value);
    }
  }
  return s.length > 120 ? `${s.slice(0, 119)}…` : s;
}
